import camelcase from 'camelcase';

/**
 * Takes a list of objects and sorts them alphabetically based on the provided keys and direction.
 * If it is an array of object, it will sort by the value of propertyPath in the object. if path is ['a', 'b'], this list will sorted by `value.a.b` .
 */
const sortAlphabetically = <T>(list: T[], direction: 'asc' | 'desc', propertyPath?: string[]): T[] => {
  const modifier = direction === 'asc' ? 1 : -1;

  return [...list].sort((a, b) => {
    let rawA: unknown = a;
    let rawB: unknown = b;

    if (propertyPath?.length) {
      rawA = propertyPath.reduce<unknown>((obj, key) => (obj as Record<string, unknown>)?.[key], a);
      rawB = propertyPath.reduce<unknown>((obj, key) => (obj as Record<string, unknown>)?.[key], b);
    }

    const aIsUndefined = rawA === null || rawA === undefined;
    const bIsUndefined = rawB === null || rawB === undefined;
    if (aIsUndefined && bIsUndefined) return 0;
    if (aIsUndefined) return 1;
    if (bIsUndefined) return -1;

    return String(rawA).localeCompare(String(rawB)) * modifier;
  });
};

function isBoolean(value: unknown): value is boolean {
  return typeof value === 'boolean';
}

const toCamel = (key: string): string => camelcase(key);

/** Convert camelCase / PascalCase to snake_case. Preserves leading underscores. */
const toSnake = (key: string): string => {
  const leading = key.match(/^_+/)?.[0] ?? '';
  const rest = key.slice(leading.length);
  return (
    leading +
    rest
      .replace(/([A-Z]+)([A-Z][a-z])/g, '$1_$2')
      .replace(/([a-z\d])([A-Z])/g, '$1_$2')
      .toLowerCase()
  );
};

const isDefined = <T>(value: T | undefined | null): value is T => value !== undefined && value !== null;

/** Replaces accents in a search string, sets it to lowercase. */
function normalizeString(str: string): string {
  return str
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

/** Returns all focusable elements within the given container. */
const getFocusableItems = (container: HTMLElement): HTMLElement[] => {
  const selector =
    'input:not([disabled]), button:not([disabled]), [tabindex]:not([tabindex="-1"]):not([disabled]), textarea:not([disabled]), select:not([disabled])';
  return Array.from(container.querySelectorAll<HTMLElement>(selector)).filter(
    (el) => el.tabIndex !== -1 && (el.getAttribute('disabled') === null || el.getAttribute('disabled') === 'false'),
  );
};

function isListEmpty(data: unknown[]): boolean {
  return data.length === 0;
}

export const Utils = {
  sortAlphabetically,
  isBoolean,
  toCamel,
  toSnake,
  isDefined,
  getFocusableItems,
  isListEmpty,
  normalizeString,
};
